import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';

let roles = [];
let editingRole = null;

async function loadRoles() {
  try {
    const res = await authFetch('/api/admin/chat-roles');
    if (!res.ok) throw new Error('加载失败');
    const data = await res.json();
    roles = data.roles || [];
  } catch (err) {
    console.error('[ai-roles] load failed:', err);
    roles = [];
  }
}

function renderRolesList() {
  const container = document.getElementById('ai-roles-tbody');
  if (!container) return;

  const systemRoles = roles.filter(r => !r.user_id);
  const customRoles = roles.filter(r => r.user_id);
  const header = createRolesListHeader(roles.length);

  if (!roles.length) {
    const empty = document.createElement('div');
    empty.className = 'ai-roles-empty empty-state';
    const message = document.createElement('p');
    message.textContent = '暂无角色配置，点击“新增角色”开始创建。';
    empty.appendChild(message);
    container.replaceChildren(header, empty);
    return;
  }

  const nodes = [header];
  if (systemRoles.length) {
    nodes.push(createRolesSectionTitle('系统角色', systemRoles.length));
    nodes.push(...systemRoles.map((role) => createRoleCard(role, false)));
  }
  if (customRoles.length) {
    nodes.push(createRolesSectionTitle('用户自定义角色', customRoles.length));
    nodes.push(...customRoles.map((role) => createRoleCard(role, true)));
  }
  container.replaceChildren(...nodes);
}

function createRolesListHeader(count) {
  const header = document.createElement('div');
  header.className = 'ai-roles-header';
  const heading = document.createElement('h3');
  heading.className = 'ai-roles-heading';
  heading.append('角色列表 ');
  const countBadge = document.createElement('span');
  countBadge.className = 'ai-roles-count';
  countBadge.textContent = String(count);
  heading.appendChild(countBadge);
  const addButton = document.createElement('button');
  addButton.id = 'ai-roles-add-btn';
  addButton.type = 'button';
  addButton.className = 'ai-roles-btn ai-roles-btn-primary';
  addButton.textContent = '新增角色';
  addButton.addEventListener('click', () => openModal());
  header.append(heading, addButton);
  return header;
}

function createRolesSectionTitle(label, count) {
  const section = document.createElement('div');
  section.className = 'ai-roles-section-title';
  section.textContent = `${label}（${count}）`;
  return section;
}

function createRoleCard(role, showUser) {
  const card = document.createElement('div');
  card.className = 'role-card';
  const cardHeader = document.createElement('div');
  cardHeader.className = 'role-card-header';
  const avatar = document.createElement('span');
  avatar.className = 'role-icon';
  avatar.textContent = role.avatar || '🤖';
  const badge = document.createElement('span');
  badge.className = `ai-roles-badge ${role.enabled ? 'ai-roles-badge-active' : 'ai-roles-badge-inactive'}`;
  badge.textContent = role.enabled ? '启用' : '禁用';
  cardHeader.append(avatar, badge);
  card.appendChild(cardHeader);

  const title = document.createElement('div');
  title.className = 'role-title';
  title.textContent = role.name || '';
  card.appendChild(title);
  const description = String(role.description || '');
  const descriptionNode = document.createElement('div');
  descriptionNode.className = 'role-desc';
  descriptionNode.textContent = `${description.slice(0, 60)}${description.length > 60 ? '...' : ''}`;
  card.appendChild(descriptionNode);
  if (showUser) {
    const owner = document.createElement('div');
    owner.className = 'role-user';
    owner.textContent = `归属：${role.user_name || role.user_email || `用户#${role.user_id}`}`;
    card.appendChild(owner);
  }
  const preview = document.createElement('div');
  preview.className = 'role-prompt-preview';
  preview.textContent = String(role.system_prompt || '').slice(0, 120);
  card.appendChild(preview);

  const actions = document.createElement('div');
  actions.className = 'role-card-actions';
  const edit = document.createElement('button');
  edit.type = 'button';
  edit.className = 'ai-roles-btn';
  edit.dataset.editId = String(role.id ?? '');
  edit.textContent = '编辑';
  edit.addEventListener('click', () => openModal(role));
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'ai-roles-btn ai-roles-btn-danger';
  remove.dataset.deleteId = String(role.id ?? '');
  remove.textContent = '删除';
  remove.addEventListener('click', () => deleteRole(role.id));
  actions.append(edit, remove);
  card.appendChild(actions);
  return card;
}

async function deleteRole(id) {
  if (!confirm('确定删除此角色？')) return;
  try {
    const res = await authFetch(`/api/admin/chat-roles/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('删除失败');
    showToast('角色已删除', 'success');
    await loadRoles();
    renderRolesList();
  } catch (err) {
    showToast(`删除失败: ${err.message}`, 'error');
  }
}

function openModal(role = null) {
  editingRole = role;
  const modal = document.getElementById('ai-roles-modal');
  if (!modal) return;

  const form = document.getElementById('ai-roles-form');
  if (form) {
    form.code.value = role?.code || '';
    form.name.value = role?.name || '';
    form.nameEn.value = role?.name_en || '';
    form.avatar.value = role?.avatar || '';
    form.description.value = role?.description || '';
    form.systemPrompt.value = role?.system_prompt || '';
    form.greeting.value = role?.greeting || '';
    form.defaultProvider.value = role?.default_provider || '';
    form.defaultModel.value = role?.default_model || '';
    form.enabled.checked = role ? !!role.enabled : true;
    form.sortOrder.value = role?.sort_order || 0;
  }

  document.getElementById('ai-roles-modal-title').textContent = role ? '编辑角色' : '新增角色';
  const templateGroup = document.getElementById('ai-roles-skill-template-group');
  if (templateGroup) templateGroup.hidden = Boolean(role);
  if (form?.copySkillTemplate) form.copySkillTemplate.value = '';
  modal.classList.add('show');
}

function closeModal() {
  const modal = document.getElementById('ai-roles-modal');
  if (modal) modal.classList.remove('show');
  editingRole = null;
}

async function saveRole() {
  const form = document.getElementById('ai-roles-form');
  if (!form) return;

  const data = {
    code: form.code.value.trim(),
    name: form.name.value.trim(),
    nameEn: form.nameEn.value.trim() || null,
    avatar: form.avatar.value.trim() || null,
    description: form.description.value.trim() || null,
    systemPrompt: form.systemPrompt.value.trim(),
    greeting: form.greeting.value.trim() || null,
    defaultProvider: form.defaultProvider.value || null,
    defaultModel: form.defaultModel.value.trim() || null,
    enabled: form.enabled.checked,
    sortOrder: parseInt(form.sortOrder.value) || 0,
  };

  // 新建时可从三角色模板复制初始 Skill 装配
  if (!editingRole && form.copySkillTemplate?.value) {
    data.copySkillTemplate = form.copySkillTemplate.value;
  }

  if (!data.code || !data.name || !data.systemPrompt) {
    showToast('代码、名称、提示词为必填项', 'error');
    return;
  }

  try {
    const url = editingRole ? `/api/admin/chat-roles/${editingRole.id}` : '/api/admin/chat-roles';
    const method = editingRole ? 'PUT' : 'POST';
    const res = await authFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || '保存失败');
    }
    const body = await res.json().catch(() => ({}));
    let msg = editingRole ? '角色已更新' : '角色已创建';
    if (body?.skillCopy?.assigned) {
      msg += `；已复制 ${body.skillCopy.assigned} 个初始技能（${(body.skillCopy.skillCodes || []).join('、')}）`;
    }
    showToast(msg, 'success');
    closeModal();
    await loadRoles();
    renderRolesList();
  } catch (err) {
    showToast('保存失败: ' + err.message, 'error');
  }
}

function createRoleFormElement(tagName, { className = '', id = '', text = '', attrs = {} } = {}) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (id) element.id = id;
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attrs)) {
    element.setAttribute(name, String(value));
  }
  return element;
}

function createRoleInput(name, { type = 'text', placeholder = '', required = false, value = '' } = {}) {
  const input = createRoleFormElement('input', { attrs: { name, type, placeholder } });
  input.required = required;
  if (value) input.value = value;
  return input;
}

function createRoleTextarea(name, placeholder, required = false) {
  const textarea = createRoleFormElement('textarea', { attrs: { name, placeholder } });
  textarea.required = required;
  return textarea;
}

function createRoleFormGroup(label, control, { id = '' } = {}) {
  const group = createRoleFormElement('div', { className: 'ai-roles-form-group', id });
  group.append(createRoleFormElement('label', { text: label }), control);
  return group;
}

function createRoleSelect(name, options) {
  const select = createRoleFormElement('select', { attrs: { name } });
  select.append(...options.map(({ value, text }) => new Option(text, value)));
  return select;
}

function createRoleFormRow(...groups) {
  const row = createRoleFormElement('div', { className: 'ai-roles-form-row' });
  row.append(...groups);
  return row;
}

function createRoleForm() {
  const form = createRoleFormElement('form', { className: 'ai-roles-form', id: 'ai-roles-form' });
  form.append(
    createRoleFormRow(
      createRoleFormGroup('角色代码 *', createRoleInput('code', { placeholder: '如: general', required: true })),
      createRoleFormGroup('角色名称 *', createRoleInput('name', { placeholder: '如: 通用助手', required: true })),
    ),
    createRoleFormRow(
      createRoleFormGroup('英文名称', createRoleInput('nameEn', { placeholder: '如: General Assistant' })),
      createRoleFormGroup('头像 URL', createRoleInput('avatar', { placeholder: '留空使用默认图标' })),
    ),
    createRoleFormGroup('角色描述', createRoleInput('description', { placeholder: '简短描述角色功能' })),
    createSkillTemplateGroup(),
    createRoleFormGroup('系统提示词 *', createRoleTextarea('systemPrompt', '定义角色的行为和能力...', true)),
    createRoleFormGroup('开场白', createRoleTextarea('greeting', '新建会话时自动显示的欢迎消息（可选）')),
    createRoleFormRow(
      createRoleFormGroup('默认 Provider（仅 MiniMax）', createRoleSelect('defaultProvider', [
        { value: '', text: '继承全局配置' }, { value: 'minimax', text: 'MiniMax' },
      ])),
      createRoleFormGroup('默认模型', createRoleInput('defaultModel', { placeholder: '留空使用全局 MiniMax 模型' })),
    ),
    createRoleFormRow(
      createRoleFormGroup('排序', createRoleInput('sortOrder', { type: 'number', value: '0' })),
      createEnabledGroup(),
    ),
  );
  const actions = createRoleFormElement('div', { className: 'ai-roles-form-actions' });
  actions.append(
    createRoleFormElement('button', { className: 'ai-roles-btn', id: 'ai-roles-cancel-btn', text: '取消', attrs: { type: 'button' } }),
    createRoleFormElement('button', { className: 'ai-roles-btn ai-roles-btn-primary', text: '保存', attrs: { type: 'submit' } }),
  );
  form.append(actions);
  return form;
}

function createSkillTemplateGroup() {
  const select = createRoleSelect('copySkillTemplate', [
    { value: '', text: '不复制（默认 0 技能，用户自行安装）' },
    { value: 'selector_king', text: '选品王：竞品研究 + 关键词挖掘' },
    { value: 'listing_scorer', text: '页面评分专家：Listing 优化 + 竞品诊断 + 差评根因' },
    { value: 'ceo_maomao', text: '贸贸：竞品研究' },
  ]);
  const group = createRoleFormGroup('初始技能模板（仅新建）', select, { id: 'ai-roles-skill-template-group' });
  group.append(createRoleFormElement('span', {
    className: 'ai-roles-skill-template-hint',
    text: '复制后用户首次使用该角色会预装对应 Skill，仍可在市场加装。',
  }));
  return group;
}

function createEnabledGroup() {
  const checkbox = createRoleInput('enabled', { type: 'checkbox' });
  checkbox.checked = true;
  const label = createRoleFormElement('label', { className: 'ai-roles-enabled-toggle', text: '启用' });
  label.prepend(checkbox);
  return createRoleFormGroup('启用状态', label);
}

function renderModal() {
  const existing = document.getElementById('ai-roles-modal');
  if (existing) return existing;

  const modal = document.createElement('div');
  modal.id = 'ai-roles-modal';
  modal.className = 'ai-roles-modal-backdrop';
  const dialog = createRoleFormElement('div', {
    className: 'ai-roles-modal',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'ai-roles-modal-title' },
  });
  dialog.append(createRoleFormElement('h2', { id: 'ai-roles-modal-title', text: '新增角色' }), createRoleForm());
  modal.append(dialog);

  document.body.appendChild(modal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  document.getElementById('ai-roles-cancel-btn')?.addEventListener('click', closeModal);
  document.getElementById('ai-roles-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    saveRole();
  });

  return modal;
}

export async function loadAiRoles() {
  renderModal();
  await loadRoles();
  renderRolesList();
}

registerViewLoader('admin-ai-roles', loadAiRoles);
